import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { formatPlacePhrase } from '@luxuryescapes/lib-location-grammar'
import moment from 'moment'

import useExperienceSearchList from 'hooks/Experiences/useExperienceSearchList'
import { useAppSelector } from 'hooks/reduxHooks'
import { INTL_LONG_MONTH, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { getExperienceSearchPath } from 'lib/experiences/experienceUtils'
import { sortOrdersByDeparture } from 'lib/order/orderUtils'
import { getUpcomingOrders } from 'selectors/orderSelectors'

import useOrderExperienceCarousels from '../ExperiencesPage/useOrderExperienceCarousels'

export interface ExperiencesHomepagePersonalisationData {
  imageId: string
  placeName: string
  propertyName: string
  stayMonth: string
  experienceCount: string
  searchUrl: string
}

function useExperiencesHomepagePersonalisationData(): ExperiencesHomepagePersonalisationData | null {
  const intl = useIntl()
  const carouselData = useOrderExperienceCarousels()
  const booking = carouselData[0]
  const offers = useAppSelector((state) => state.offer.offers)
  const upcomingOrders = useAppSelector((state) => getUpcomingOrders(state))

  const firstOrder = useMemo(() => {
    const hotelOrders = Object.values(upcomingOrders).filter(
      (order) => order.items.length > 0 || order.bedbankItems.length > 0,
    )
    return sortOrdersByDeparture(hotelOrders)[0]
  }, [upcomingOrders])

  const offerId = firstOrder?.items[0]?.offerId ?? firstOrder?.bedbankItems[0]?.offer.id

  const imageId = useMemo(() => {
    if (!firstOrder) {
      return undefined
    }

    const offerImageId = offerId ? offers[offerId]?.images?.[0]?.id : undefined
    if (offerImageId) {
      return offerImageId
    }

    return firstOrder.items[0]?.offer?.image?.id
  }, [firstOrder, offerId, offers])

  const searchDates = useMemo(() => {
    if (!booking) {
      return undefined
    }

    return {
      from: moment(booking.dates.checkIn).subtract(7, 'days').format(ISO_DATE_FORMAT),
      to: moment(booking.dates.checkOut).add(7, 'days').format(ISO_DATE_FORMAT),
    }
  }, [booking])

  const experienceList = useExperienceSearchList({
    placeId: booking?.place.id,
    from: searchDates?.from,
    to: searchDates?.to,
    sortBy: 'type',
  })

  const experienceTotal = experienceList.ids.length

  const searchUrl = useMemo(() => {
    if (!booking || !searchDates) {
      return ''
    }

    return getExperienceSearchPath(booking.place, {
      checkIn: searchDates.from,
      checkOut: searchDates.to,
    })
  }, [booking, searchDates])

  return useMemo(() => {
    if (!booking || !imageId || experienceList.fetching || !experienceTotal) {
      return null
    }

    return {
      imageId,
      placeName: formatPlacePhrase({
        location: { name: booking.place.name },
        language: intl.locale,
        intent: 'location',
      }),
      propertyName: booking.propertyName,
      stayMonth: intlDateFormatter(new Date(booking.dates.checkIn), INTL_LONG_MONTH, intl.locale, 'UTC'),
      experienceCount: intl.formatNumber(experienceTotal),
      searchUrl,
    }
  }, [booking, imageId, experienceList.fetching, experienceTotal, intl, searchUrl])
}

export default useExperiencesHomepagePersonalisationData
